// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * DashboardPage
 *
 * Main dashboard view for advisors showing KPIs, alerts, activity feed,
 * and navigation shortcuts. Supports admin view toggle for brokerage-wide data.
 */

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Spinner, Button } from '@loa/ui-components';
import { useAdvisor } from '../contexts/AdvisorContext';
import { useAdminViewToggle } from '../hooks/useAdminViewToggle';
import {
  useDashboard,
  useDashboardAlerts,
  useDashboardInsights,
  useAdvisorPerformance,
  useResendInvite,
  useSendReminder,
} from '../hooks/useDashboard';

import { AdvisorFilter } from '../components/AdvisorFilter';
import {
  DashboardAlertsPanel,
  DashboardAdvisorPerformance,
} from '../components/dashboard';
import { coaApi } from '../api/coaApi';
import { profileRequestApi } from '../api/profileRequestApi';
import { RequestOverviewCard } from '../components/dashboard/RequestOverviewCard';
import { ProposalOverviewCard } from '../components/dashboard/ProposalOverviewCard';
import { DashboardCalendarPanel } from '../components/dashboard/DashboardCalendarPanel';
import type { DashboardAlert, AlertAction, AdvisorMetric, SortColumn, SortDirection } from '../types/dashboard';
import { usePageContextRegister } from '../contexts/PageContextProvider';
export function DashboardPage() {
  usePageContextRegister({ pageType: 'DASHBOARD' });
  const navigate = useNavigate();
  const { advisor, loading: advisorLoading, error: advisorError } = useAdvisor();
  const {
    isAdminView,
    isInitializing,
  } = useAdminViewToggle({ storageKey: 'dashboard' });

  // Advisor filter state (for admin view)
  const [selectedAdvisorId, setSelectedAdvisorId] = useState<string>('all');

  // Determine API parameters based on view mode
  const all = isAdminView && selectedAdvisorId === 'all';
  const advisorId = isAdminView && selectedAdvisorId !== 'all' ? selectedAdvisorId : undefined;

  // Dashboard data hooks
  const {loading: summaryLoading, error: summaryError, refreshAll} = useDashboard({
    all,
    advisorId,
    skip: isInitializing,
  });

  const { data: alertsData, loading: alertsLoading, error: alertsError, refetch: refetchAlerts } = useDashboardAlerts({
    all,
    advisorId,
    limit: 4,
    skip: isInitializing,
  });

  const { data: insightsData } = useDashboardInsights({
    all,
    advisorId,
    limit: 4,
    skip: isInitializing,
  });

  // Advisor performance (admin only)
  const [performanceSortBy, setPerformanceSortBy] = useState<SortColumn>('profileCount');
  const [performanceSortDir, setPerformanceSortDir] = useState<SortDirection>('desc');

  const { data: performanceData, loading: performanceLoading, error: performanceError, refetch: refetchPerformance } = useAdvisorPerformance({
    limit: 10,
    sortBy: performanceSortBy,
    sortDir: performanceSortDir,
    skip: !isAdminView || isInitializing,
  });

  // Action hooks
  const { resend: resendInvite } = useResendInvite();
  const { sendReminder } = useSendReminder();

  const handleAlertAction = useCallback(async (alert: DashboardAlert, action: AlertAction) => {
    if (action.method === 'NAVIGATE' && action.href) {
      navigate(action.href);
      return;
    }

    if (action.method === 'POST') {
      try {
        if (action.type === 'RESEND_INVITE' && alert.resourceType === 'CLIENT') {
          await resendInvite(alert.resourceId);
          refetchAlerts();
        } else if (action.type === 'SEND_REMINDER' && alert.resourceType === 'LOA') {
          await sendReminder(alert.resourceId);
          refetchAlerts();
        } else if (action.type === 'SEND_REMINDER' && alert.resourceType === 'COA') {
          // CoA-scoped SEND_REMINDER dispatches a nudge to every pending
          // provider on the CoA. Routed through the unified
          // /api/requests/{id}/nudge-provider — the alert payload still
          // carries `resourceId = coaId`, so we resolve the linked
          // ProfileRequest first via coaApi.getCoA.
          const coa = await coaApi.getCoA(alert.resourceId);
          if (coa.requestId) {
            await profileRequestApi.nudgeProvider(coa.requestId);
            refetchAlerts();
          }
        }
      } catch (error) {
        console.error('Alert action failed:', error);
      }
    }
  }, [navigate, resendInvite, sendReminder, refetchAlerts]);

  const handleViewAllAlerts = useCallback(() => {
    navigate('/activity-log');
  }, [navigate]);

  const handleAdvisorClick = useCallback((advisorMetric: AdvisorMetric) => {
    // Filter dashboard by this advisor
    setSelectedAdvisorId(advisorMetric.advisorId);
  }, []);

  const handlePerformanceSortChange = useCallback((column: SortColumn, direction: SortDirection) => {
    setPerformanceSortBy(column);
    setPerformanceSortDir(direction);
    refetchPerformance();
  }, [refetchPerformance]);

  if (advisorLoading || isInitializing) {
    return (
      <div style={{ marginTop: '3em' }}>
        <Spinner />
      </div>
    );
  }

  if (advisorError) {
    return (
      <div style={{ marginTop: '3em' }}>
        <Alert variant="error" header="Error Loading Account">
          <p>{advisorError}</p>
        </Alert>
      </div>
    );
  }

  if (!advisor) {
    return (
      <div style={{ marginTop: '3em' }}>
        <Alert variant="warning" header="No Account Found">
          <p>Please contact your administrator.</p>
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 24px' }}>
      {isAdminView && (
        <div style={{ marginBottom: '1.5em' }}>
          <AdvisorFilter
            value={selectedAdvisorId}
            onChange={setSelectedAdvisorId}
            disabled={summaryLoading}
          />
        </div>
      )}

      {summaryError && (
        <Alert variant="error" header="Error Loading Dashboard" style={{ marginBottom: '1.5em' }}>
          <p>{summaryError}</p>
          <Button variant="outlined" onClick={refreshAll}>Try Again</Button>
        </Alert>
      )}

      {/* Top row per Figma "Advisor Dashboard" (node 426-5855): alerts/
          insights on the left, the Calendar panel top-right — equal-width
          columns that stack below xl. The Calendar panel replaces the old
          Today's Meetings widget AND the standalone /calendar page, and
          renders its own empty / re-auth states when no connection is
          present, so it's safe to mount unconditionally. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-lg items-start">
        <DashboardAlertsPanel
          alerts={alertsData?.alerts ?? []}
          totalCount={alertsData?.totalCount ?? 0}
          urgentCount={alertsData?.urgentCount ?? 0}
          warningCount={alertsData?.warningCount ?? 0}
          infoCount={alertsData?.infoCount ?? 0}
          insights={insightsData?.insights ?? []}
          insightsCount={insightsData?.totalCount ?? 0}
          maxDisplay={4}
          loading={alertsLoading}
          error={alertsError}
          onAction={handleAlertAction}
          onViewAll={handleViewAllAlerts}
        />
        <DashboardCalendarPanel />
      </div>

      <div style={{ marginTop: '1.5em' }}>
        <RequestOverviewCard />
      </div>

      <div style={{ marginTop: '1.5em' }}>
        <ProposalOverviewCard />
      </div>

      {/* Advisor Performance Table (Admin View Only) */}
      {isAdminView && (
        <div style={{ marginTop: '2em' }}>
          <DashboardAdvisorPerformance
            advisors={performanceData?.advisors ?? []}
            loading={performanceLoading}
            error={performanceError}
            onAdvisorClick={handleAdvisorClick}
            onSortChange={handlePerformanceSortChange}
          />
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
